import { dehydrate, QueryClient } from '@tanstack/react-query';
import {
    Body,
    Box,
    Flex,
    Grid,
    Heading,
    HtmlContent,
    IconAdd,
    IconExternalLink,
    IconRemove,
    ListDivider,
    Meta,
    PageCard,
    PersonHero,
    Stack,
    Tag,
    TextButton,
    useTheme,
    VStack,
    Wrapper,
} from 'designsystem';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { PersonDetailQuery, PersonDetailQueryVariables, usePersonDetailQuery } from '../../../gql/api';
import { FormattedMessage, useIntl } from 'react-intl';
import React, { useEffect, useMemo, useState } from 'react';
import { apiImageToOpenGraph, authOptions, fetchApiData, gtm, slugifyTitle, useGetApiImageProps } from 'shared';
import loadIntlMessages from '../../../utils/loadIntlMessages';
import groupBy from 'lodash.groupby';
import Link from 'next/link';
import prefetchGlobals from '../../../utils/prefetchGlobals';
import { unstable_getServerSession } from 'next-auth';
import { NextSeo } from 'next-seo';
import striptags from 'striptags';
import fetchArchiveUri from '../../../utils/fetchArchiveUri';
import InstituteBreadcrumbs from '../../../components/InstituteBreadcrumbs';
import { useArchivePathQuery } from '../../../gql/cms';
import siteHandleByLocale from '../../../utils/siteHandleByLocale';
import { allowedEditionTypes } from '../../../constants/relevantEditionTypes';

const getFilmListItems = (personFilms: PersonDetailQuery['person']['films']) => {
    const groupedById = groupBy(personFilms, ({ film }) => film.id);
    return Object.keys(groupedById)
        .map(id => ({
            film: {
                id,
                title: groupedById[id][0].film.fullPreferredTitle,
                year: groupedById[id][0].film.edition.year,
            },
            roles: groupedById[id].map(personFilm => personFilm.role.translation),
            editionName: groupedById[id][0].film.edition.name,
            editionType: groupedById[id][0].film.edition.editionType.description,
        }))
        .filter(group => !allowedEditionTypes.includes(group.editionType))
        .sort((a, b) => b.film.year - a.film.year);
};

const PersonArchiveDetail = () => {
    const [showFullBio, setShowFullBio] = useState(false);
    const { query, asPath } = useRouter();
    const theme = useTheme();
    const { locale } = useIntl();
    const { data } = usePersonDetailQuery({
        id: query.id as string,
        language: locale,
    });
    const archiveUri = useArchivePathQuery({
        site: siteHandleByLocale[locale],
    }).data.entry.uri;
    const person = data?.person;
    const filmListItems = useMemo(() => getFilmListItems(person?.films), [person?.films]);
    const getImageProps = useGetApiImageProps();
    const heroImgProps = useMemo(
        () => getImageProps(person?.publications.favoriteImage, person?.fullName),
        [person?.fullName, person?.publications.favoriteImage, getImageProps]
    );
    const ogImage = apiImageToOpenGraph(person?.publications.favoriteImage);

    useEffect(() => {
        gtm.pageView(asPath, {
            page_pillar: 'institute',
            page_navigation: archiveUri,
            page_language: locale,
        });
    }, [archiveUri, asPath, locale]);

    return (
        <>
            {person && (
                <NextSeo
                    title={person.fullName}
                    description={striptags(person.intro?.translation)}
                    openGraph={{
                        images: ogImage,
                    }}
                    titleTemplate="%s | IDFA Archive"
                />
            )}
            <Wrapper pt={7}>
                <Box ml={[0, null, null, null, 11]} mb={[12, null, '112px']}>
                    <InstituteBreadcrumbs
                        parentUri={archiveUri}
                        currentPage={{
                            title: person?.fullName || '',
                            uri: asPath,
                        }}
                    />
                </Box>
                <PersonHero image={person?.publications?.favoriteImage?.jpeg?.length > 0 ? heroImgProps : null}>
                    <Stack spacing={[1, null, 3]}>
                        {person.professions && <Tag>{person.professions.translation}</Tag>}
                        <Heading variant={1} as="h1">
                            {person.fullName}
                        </Heading>
                        {person.nationality && (
                            <Meta
                                size="m"
                                items={[
                                    {
                                        key: 'countries',
                                        value: person.nationality.translation,
                                    },
                                ]}
                            />
                        )}
                    </Stack>
                </PersonHero>

                <Grid>
                    <Box
                        gridColumn={['1 / -1', null, null, '1 / span 7', '2 / span 6']}
                        maxWidth="576px"
                        mt={[8]}
                        mb={[9]}
                    >
                        {person.intro && (
                            <Body fontSize={5}>
                                <HtmlContent html={person.intro.translation} />
                            </Body>
                        )}
                        {person.bio && (
                            <>
                                <Heading mt={9} mb={[4, null, 6]} variant={3}>
                                    <FormattedMessage description="Person: heading bio" defaultMessage="Biografie" />
                                </Heading>

                                <Body noOfLines={showFullBio ? 0 : 5} mb={4}>
                                    <HtmlContent html={person.bio.translation} />
                                </Body>

                                <TextButton
                                    iconRight={null}
                                    iconLeft={
                                        <Box color={theme.tokens.SyntaxPrimaryColorDefault} w={6} h={6}>
                                            {showFullBio ? <IconRemove /> : <IconAdd />}
                                        </Box>
                                    }
                                    onClick={() => setShowFullBio(!showFullBio)}
                                >
                                    <Body fontSize={4} color={theme.tokens.SyntaxPrimaryColorDefault}>
                                        {showFullBio ? (
                                            <FormattedMessage
                                                description="Person: read less bio"
                                                defaultMessage="Sluit de volledige biografie"
                                            />
                                        ) : (
                                            <FormattedMessage
                                                description="Person: read more bio"
                                                defaultMessage="Lees de volledige biografie"
                                            />
                                        )}
                                    </Body>
                                </TextButton>
                            </>
                        )}
                    </Box>
                    {filmListItems.length > 0 && (
                        <Box gridColumn={['1 / -1', null, null, null, '2 / -2']} mt={[6]} mb={[9]}>
                            <Heading variant={3} mb={[4, null, 6]}>
                                <FormattedMessage defaultMessage="Festival history" />
                            </Heading>
                            <VStack
                                divider={<ListDivider />}
                                borderTop="1px solid"
                                borderBottom="1px solid"
                                borderColor="neutral.30"
                                spacing={0}
                            >
                                {filmListItems.map(filmListItem => (
                                    <Flex
                                        flexDirection={['column', 'row']}
                                        justifyContent="space-between"
                                        key={filmListItem.film.id}
                                        py={4}
                                        pl={[0, null, 2]}
                                        w="100%"
                                    >
                                        <Box flex={1} mb={[2, null, 0]} pr={[0, null, 3]}>
                                            <Body display={['block', null, 'none']} color="grey.60">
                                                <FormattedMessage defaultMessage="Film" />
                                            </Body>
                                            <Link
                                                href={`/film/${filmListItem.film.id}/${slugifyTitle(
                                                    filmListItem.film.title
                                                )}`}
                                            >
                                                <Body textDecoration="underline" color="darkblue.60" clamp={1}>
                                                    {filmListItem.film.title}
                                                </Body>
                                            </Link>
                                        </Box>
                                        <Box flex={1} mb={[2, null, 0]}>
                                            <Body display={['block', null, 'none']} color="grey.60">
                                                <FormattedMessage defaultMessage="Rol" />
                                            </Body>
                                            <Body>{filmListItem.roles.join(', ')}</Body>
                                        </Box>
                                        <Box flex={1}>
                                            <Body display={['block', null, 'none']} color="grey.60">
                                                <FormattedMessage defaultMessage="In" />
                                            </Body>
                                            <Body>{filmListItem.editionName}</Body>
                                        </Box>
                                    </Flex>
                                ))}
                            </VStack>
                        </Box>
                    )}

                    {person?.publications?.links && person?.publications.links.length > 0 && (
                        <Stack
                            gridColumn={['1 / -1', null, null, null, '2 / -2']}
                            spacing={[4, null, 6]}
                            mt={[6]}
                            mb={[9]}
                        >
                            <Heading variant={3}>
                                <FormattedMessage defaultMessage="Op het web" />
                            </Heading>
                            <Box
                                display="grid"
                                gridTemplateColumns={['repeat(1, 1fr)', 'repeat(2, 1fr)', null, 'repeat(3, 1fr)']}
                                gap={4}
                            >
                                {person?.publications.links.map(link => (
                                    <Box minW={[0, null, '33%']} key={link.value}>
                                        <a href={link.value} target="_blank" rel="noreferrer">
                                            <PageCard
                                                size="s"
                                                variant="white"
                                                title={link.title}
                                                textButtonProps={{
                                                    iconRight: <IconExternalLink />,
                                                }}
                                            />
                                        </a>
                                    </Box>
                                ))}
                            </Box>
                        </Stack>
                    )}
                </Grid>
            </Wrapper>
        </>
    );
};
export const getServerSideProps: GetServerSideProps = async ({ params, locale, req, res }) => {
    const queryClient = new QueryClient();

    const session = await unstable_getServerSession(req, res, authOptions);
    const variables = {
        id: params?.id as string,
        language: locale,
    };
    await queryClient.prefetchQuery(usePersonDetailQuery.getKey(variables), () =>
        fetchApiData<PersonDetailQuery, PersonDetailQueryVariables>({
            locale,
            query: usePersonDetailQuery.document,
            variables,
            session,
        })
    );

    const archiveUri = await fetchArchiveUri(queryClient, locale);
    await prefetchGlobals(queryClient, locale, { pathType: 'PARENT_URI', uri: archiveUri });

    return {
        props: {
            intlMessages: await loadIntlMessages(locale),
            dehydratedState: dehydrate(queryClient),
            session,
        },
    };
};

export default PersonArchiveDetail;
